/**
 * Serviço para pré-carregar e gerenciar assets da aplicação
 * Otimizado para dispositivos móveis e suporte offline
 */
class AssetPreloader {
  constructor() {
    // Mapear assets por rota
    this.routeAssets = {
      '/': ['logo.svg', 'background.webp'],
      '/tela1': ['tela1/background.webp', 'tela1/central-image-sem-logo.webp'],
      '/tela1-1': ['tela1-1/background.webp', 'tela1-1/splashpapaimamae.webp', 'tela1-1/botaovamosla.webp'],
      '/tela2': ['tela2/fundopuro.webp', 'tela2/foguetinho.webp'],
      '/tela3': ['tela3/tela3fundo.webp', 'tela3/tela3alvo.webp'],
      '/tela4': ['tela4/tela4-background.webp', 'tela4/tela4imagem.webp'],
      '/tela5': ['tela5/background.png', 'tela5/icon.png'],
      '/tela5-0-categorias': ['tela5-0-categorias/tela5fundonovo.webp', 'tela5-0-categorias/animais-btn.webp'],
      '/tela5-1-temas': ['tela5-1-temas/background-Letras.webp', 'tela5-1-temas/btn1-Letras.webp'],
      '/tela6': ['tela6/tela6fundo.webp', 'tela6/tela6imagem.webp'],
      '/tela7-0-play': ['tela7-0-play/bannertopoplay.webp', 'tela7-0-play/interacao.webp', 'tela7-0-play/loading.svg'],
    };
    
    // Mapeamento de rotas prováveis para cada rota atual
    this.predictiveRoutes = {
      '/': ['/tela1'],
      '/tela1': ['/tela1-1'],
      '/tela1-1': ['/tela2'],
      '/tela2': ['/tela3'],
      '/tela3': ['/tela4'],
      '/tela4': ['/tela5'],
      '/tela5': ['/tela5-0-categorias', '/tela5-1-temas'],
      '/tela5-0-categorias': ['/tela5-1-temas', '/tela6'],
      '/tela5-1-temas': ['/tela6', '/tela7-0-play'],
      '/tela6': ['/tela7-0-play'],
    };
    
    // Prioridades dos assets por rota
    this.assetPriorities = {
      '/': {
        'logo.svg': 'high',
        'background.webp': 'high'
      },
      '/tela1': {
        'tela1/background.webp': 'high',
        'tela1/central-image-sem-logo.webp': 'high'
      },
      '/tela1-1': {
        'tela1-1/background.webp': 'high',
        'tela1-1/splashpapaimamae.webp': 'high',
        'tela1-1/botaovamosla.webp': 'high'
      },
      '/tela2': {
        'tela2/fundopuro.webp': 'high',
        'tela2/foguetinho.webp': 'high'
      },
      '/tela3': {
        'tela3/tela3fundo.webp': 'high',
        'tela3/tela3alvo.webp': 'high'
      },
      '/tela4': {
        'tela4/tela4-background.webp': 'high',
        'tela4/tela4imagem.webp': 'high'
      },
      '/tela5-0-categorias': {
        'tela5-0-categorias/tela5fundonovo.webp': 'high',
        'tela5-0-categorias/animais-btn.webp': 'high'
      },
      '/tela5-1-temas': {
        'tela5-1-temas/background-Letras.webp': 'high',
        'tela5-1-temas/btn1-Letras.webp': 'high'
      },
      '/tela6': {
        'tela6/tela6fundo.webp': 'high',
        'tela6/tela6imagem.webp': 'high'
      },
      '/tela7-0-play': {
        'tela7-0-play/bannertopoplay.webp': 'high',
        'tela7-0-play/interacao.webp': 'high'
      }
    };
    
    // Status do cache de cada rota
    this.cacheStatus = {};
    
    // Lista de pré-carregamento atual
    this.currentPreloadQueue = [];
    
    // Estado de conexão atual
    this.isOnline = navigator.onLine;
    
    // Listener para mudanças de estado de rede
    window.addEventListener('online', this.handleConnectionChange.bind(this));
    window.addEventListener('offline', this.handleConnectionChange.bind(this));
    
    // Estado de economia de dados 
    this.dataSaving = this.isDataSavingEnabled();
    
    // Detectar qualidade da conexão
    this.connectionType = this.getConnectionType();
    
    // Cache para armazenar blobs de assets
    this.assetCache = new Map();
    
    // Flag para indicar se a inicialização já foi concluída
    this.initialized = false;
    
    // Flag para rastreamento de erros críticos
    this.hasCriticalError = false;
    
    // Carregar assets essenciais imediatamente
    this.preloadEssentialAssets();
    
    // Inicialização do service worker para cache offline
    this.initServiceWorker();
  }
  
  /**
   * Pré-carrega assets para rotas que o usuário provavelmente visitará em seguida
   * @param {string} currentPath - Caminho atual do usuário
   */
  preloadPredictiveAssets(currentPath) {
    // Se não estiver online ou em modo de economia de dados, não fazer preload preditivo
    if (!this.isOnline || this.dataSaving) {
      console.log('Preload preditivo desativado: dispositivo offline ou em modo de economia de dados');
      return;
    }
    
    // Obter rotas prováveis com base na rota atual
    const nextProbableRoutes = this.predictiveRoutes[currentPath] || [];
    
    if (nextProbableRoutes.length === 0) {
      console.log(`Sem rotas preditivas definidas para ${currentPath}`);
      return;
    }
    
    console.log(`Pré-carregando preditivamente para rota atual ${currentPath}:`, nextProbableRoutes);
    
    // Determinar nível de concorrência baseado na conexão
    // Usar metade da concorrência normal para não afetar a experiência atual
    const concurrency = Math.max(1, Math.floor(this.getOptimalConcurrency() / 2));
    
    // Precarregar assets das rotas prováveis em segundo plano
    setTimeout(() => {
      this.preloadMultipleRoutes(nextProbableRoutes, concurrency)
        .catch(error => console.warn('Erro em preload preditivo:', error));
    }, 500);
  }
  
  /**
   * Carrega os assets essenciais para inicialização do app
   */
  async preloadEssentialAssets() {
    try {
      // Carregar assets da tela inicial com prioridade
      const essentialAssets = this.routeAssets['/'].concat(this.routeAssets['/tela1']);
      
      // Carregar primeiro os assets de alta prioridade
      const highPriorityAssets = essentialAssets.filter(asset => 
        this.getAssetPriority(asset) === 'high'
      );
      
      // Limitar concorrência para otimizar em conexões móveis
      const concurrency = this.getOptimalConcurrency();
      
      // Carregar assets de alta prioridade primeiro
      await this.loadAssetsWithConcurrency(highPriorityAssets, concurrency);
      
      // Marcar como inicializado
      this.initialized = true;
      
    } catch (error) {
      console.error('Erro ao carregar assets essenciais:', error);
      this.hasCriticalError = true;
    }
  }
  
  /**
   * Carrega um conjunto de assets com limite de concorrência
   */
  async loadAssetsWithConcurrency(assets, concurrency = 3) {
    // Clonar array para não modificar o original
    const queue = [...assets];
    const inProgress = new Set();
    const results = [];
    
    // Continuar até esvaziar a fila
    while (queue.length > 0 || inProgress.size > 0) {
      // Preencher até o limite de concorrência
      while (queue.length > 0 && inProgress.size < concurrency) {
        const asset = queue.shift();
        inProgress.add(asset);
        
        // Carregar asset e remover da lista em progresso quando concluído
        this.loadAsset(asset)
          .then(result => {
            results.push({ asset, success: true, result });
            inProgress.delete(asset);
          })
          .catch(error => {
            console.warn(`Erro ao carregar asset ${asset}:`, error);
            results.push({ asset, success: false, error });
            inProgress.delete(asset);
          });
      }
      
      // Aguardar um pouco antes de verificar novamente
      if (inProgress.size >= concurrency || (queue.length === 0 && inProgress.size > 0)) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }
    
    return results;
  }
  
  /**
   * Determina o nível de concorrência ideal com base na conexão
   */
  getOptimalConcurrency() {
    // Default para conexão desconhecida
    let concurrency = 3;
    
    // Se estamos em modo de economia de dados, reduzir concorrência
    if (this.dataSaving) {
      return 1;
    }
    
    // Ajustar com base na qualidade da conexão
    switch (this.connectionType) {
      case 'slow-2g':
      case '2g':
        concurrency = 1;
        break;
      case '3g':
        concurrency = 2;
        break;
      case '4g':
      case 'wifi':
        concurrency = 4;
        break;
      default:
        concurrency = 3;
    }
    
    return concurrency;
  }
  
  /**
   * Retorna a prioridade de um asset (high, medium, low)
   */
  getAssetPriority(asset) {
    // Verificar prioridades definidas
    for (const route in this.assetPriorities) {
      if (this.assetPriorities[route][asset]) {
        return this.assetPriorities[route][asset];
      }
    }
    
    // Definir prioridade com base em heurísticas
    if (asset.includes('background')) return 'high';
    if (asset.includes('logo')) return 'high';
    if (asset.endsWith('.svg')) return 'medium';
    
    // Padrão
    return 'medium';
  }
  
  /**
   * Obtém o tipo de conexão do dispositivo
   */
  getConnectionType() {
    const connection = navigator.connection || 
                      navigator.mozConnection || 
                      navigator.webkitConnection;
    
    if (connection) {
      if (connection.effectiveType) {
        return connection.effectiveType; // slow-2g, 2g, 3g, 4g
      }
      
      if (connection.type) {
        return connection.type; // wifi, cellular, etc
      }
    }
    
    // Não foi possível determinar
    return 'unknown';
  }
  
  /**
   * Verifica se o modo de economia de dados está ativo
   */
  isDataSavingEnabled() {
    if (navigator.connection) {
      return navigator.connection.saveData;
    }
    return false;
  }
  
  /**
   * Inicializa o service worker para cache offline
   */
  async initServiceWorker() {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/service-worker.js');
        console.log('Service Worker registrado com sucesso:', registration);
      } catch (error) {
        console.error('Erro ao registrar Service Worker:', error);
      }
    }
  }
  
  /**
   * Manipula mudanças no estado de conexão
   */
  handleConnectionChange() {
    const wasOnline = this.isOnline;
    this.isOnline = navigator.onLine;
    
    // Atualizar tipo de conexão
    this.connectionType = this.getConnectionType();
    
    // Verificar mudança de estado
    if (!wasOnline && this.isOnline) {
      // Acabou de ficar online, sincronizar assets faltantes
      this.syncMissingAssets();
    } else if (wasOnline && !this.isOnline) {
      // Acabou de ficar offline, verificar assets críticos em cache
      this.checkCriticalAssets();
    }
    
    // Atualizar estado de economia de dados
    this.dataSaving = this.isDataSavingEnabled();
    
    // Disparar evento personalizado para informar outros componentes
    window.dispatchEvent(new CustomEvent('connectionChange', {
      detail: {
        online: this.isOnline,
        connectionType: this.connectionType,
        dataSaving: this.dataSaving
      }
    }));
  }
  
  /**
   * Sincroniza assets faltantes quando o dispositivo volta a ficar online
   */
  async syncMissingAssets() {
    if (!this.isOnline) return;
    
    // Obter as rotas mais importantes para sincronizar primeiro
    const criticalRoutes = ['/', '/tela1', '/tela1-1'];
    
    for (const route of criticalRoutes) {
      const status = this.getRouteAssetsStatus(route);
      
      // Se a rota não está 100% em cache, pré-carregar
      if (status.cachePercentage < 100) {
        await this.preloadRouteAssets(route);
      }
    }
  }
  
  /**
   * Verifica se os assets críticos estão disponíveis offline
   */
  async checkCriticalAssets() {
    const criticalRoutes = ['/', '/tela1', '/tela1-1'];
    
    for (const route of criticalRoutes) {
      const status = this.getRouteAssetsStatus(route);
      if (status.cachePercentage < 80) {
        console.warn(`Assets críticos para a rota ${route} não estão totalmente em cache (${status.cachePercentage}%)`);
      }
    }
  }
  
  /**
   * Carrega um asset e o armazena em cache
   * @param {string} asset - Caminho do asset
   * @returns {Promise<Blob>} - Blob do asset carregado
   */
  async loadAsset(asset) {
    // Verificar se o asset já está em cache
    if (this.assetCache.has(asset)) {
      return this.assetCache.get(asset);
    }
    
    // Se estamos offline, tentar usar cache do service worker
    if (!this.isOnline) {
      try {
        const cache = await caches.open('assets-cache');
        const response = await cache.match(`/assets/${asset}`);
        
        if (response) {
          const blob = await response.blob();
          this.assetCache.set(asset, blob);
          return blob;
        } else {
          throw new Error(`Asset ${asset} não encontrado no cache`);
        }
      } catch (error) {
        console.error(`Erro ao carregar asset ${asset} do cache:`, error);
        throw error;
      }
    }
    
    // Carregar asset da rede
    try {
      // Construir URL completa do asset
      const assetUrl = `/assets/${asset}`;
      
      // Parâmetros de fetch para garantir carregamento fresco
      const fetchOptions = {
        cache: 'no-cache', // Força revalidação com o servidor
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      };
      
      // Se estamos em modo de economia de dados, carregar versão de baixa qualidade
      if (this.dataSaving) {
        // Verificar se existe uma versão de baixa qualidade do asset
        const lowQualityAsset = assetUrl.replace(/\.(png|jpg|jpeg)$/, '-low.$1');
        
        try {
          const checkResponse = await fetch(lowQualityAsset, { method: 'HEAD' });
          if (checkResponse.ok) {
            // Se existe versão de baixa qualidade, usar ela
            const response = await fetch(lowQualityAsset, fetchOptions);
            const blob = await response.blob();
            this.assetCache.set(asset, blob);
            return blob;
          }
        } catch (error) {
          // Versão de baixa qualidade não existe, continuar com original
          console.log(`Versão de baixa qualidade não disponível para ${asset}`);
        }
      }
      
      // Carregar versão normal
      const response = await fetch(assetUrl, fetchOptions);
      
      if (!response.ok) {
        throw new Error(`Falha ao carregar asset: ${response.status} ${response.statusText}`);
      }
      
      const blob = await response.blob();
      
      // Armazenar em cache
      this.assetCache.set(asset, blob);
      
      // Também armazenar no cache do service worker
      try {
        const cache = await caches.open('assets-cache');
        await cache.put(`/assets/${asset}`, new Response(blob));
      } catch (error) {
        console.warn(`Erro ao armazenar asset ${asset} no cache do service worker:`, error);
      }
      
      return blob;
    } catch (error) {
      console.error(`Erro ao carregar asset ${asset}:`, error);
      throw error;
    }
  }
  
  /**
   * Pré-carrega todos os assets ou um conjunto específico
   * @param {Function} onComplete - Callback quando concluir
   * @param {Object} options - Opções de carregamento
   */
  preloadAll(onComplete, options = {}) {
    const {
      background = false,
      concurrency = this.getOptimalConcurrency(),
      routes = null
    } = options;
    
    // Se a inicialização já foi concluída, podemos carregar assets secundários
    const preloadRoutesPromise = async () => {
      try {
        // Determinar quais rotas carregar
        let routesToLoad = routes;
        
        if (!routesToLoad) {
          // Primeiro as rotas críticas
          const criticalRoutes = ['/', '/tela1', '/tela1-1'];
          await this.preloadMultipleRoutes(criticalRoutes, concurrency);
          
          // Depois as outras rotas se estiver em segundo plano
          if (background) {
            const otherRoutes = Object.keys(this.routeAssets)
              .filter(route => !criticalRoutes.includes(route));
            
            // Carregar outras rotas em segundo plano
            setTimeout(() => {
              this.preloadMultipleRoutes(otherRoutes, concurrency)
                .catch(error => console.warn('Erro em carregamento de fundo:', error));
            }, 1000);
          }
        } else {
          // Carregar apenas as rotas solicitadas
          await this.preloadMultipleRoutes(routesToLoad, concurrency);
        }
        
        if (onComplete && typeof onComplete === 'function') {
          onComplete();
        }
      } catch (error) {
        console.error('Erro durante preloadAll:', error);
        
        // Ainda chamar callback para não bloquear o app
        if (onComplete && typeof onComplete === 'function') {
          onComplete();
        }
      }
    };
    
    // Se a inicialização já ocorreu ou se estamos em modo de carregamento em segundo plano
    if (this.initialized || background) {
      preloadRoutesPromise();
    } else {
      // Aguardar carregamento dos assets essenciais primeiro
      this.preloadEssentialAssets()
        .then(() => {
          this.initialized = true;
          return preloadRoutesPromise();
        })
        .catch(error => {
          console.error('Erro na inicialização:', error);
          // Ainda chamar callback para não bloquear o app
          if (onComplete && typeof onComplete === 'function') {
            onComplete();
          }
        });
    }
  }
  
  /**
   * Pré-carrega múltiplas rotas com controle de concorrência
   */
  async preloadMultipleRoutes(routes, concurrency) {
    // Carregar assets de alta prioridade primeiro
    const highPriorityAssets = [];
    
    // Coletar todos os assets de alta prioridade das rotas
    routes.forEach(route => {
      if (this.routeAssets[route]) {
        const routeHighPriorityAssets = this.routeAssets[route].filter(asset => 
          this.getAssetPriority(asset) === 'high'
        );
        highPriorityAssets.push(...routeHighPriorityAssets);
      }
    });
    
    // Carregar primeiro os assets de alta prioridade
    await this.loadAssetsWithConcurrency(highPriorityAssets, concurrency);
    
    // Depois carregar os assets restantes
    const allAssets = routes.reduce((acc, route) => {
      if (this.routeAssets[route]) {
        return [...acc, ...this.routeAssets[route]];
      }
      return acc;
    }, []);
    
    // Filtrar para remover os de alta prioridade já carregados
    const remainingAssets = allAssets.filter(asset => 
      !highPriorityAssets.includes(asset) && !this.assetCache.has(asset)
    );
    
    // Carregar os assets restantes
    await this.loadAssetsWithConcurrency(remainingAssets, concurrency);
    
    // Atualizar status de cache para cada rota
    routes.forEach(route => {
      this.updateCacheStatus(route);
    });
  }
  
  /**
   * Atualiza o status de cache para uma rota
   */
  updateCacheStatus(route) {
    if (!this.routeAssets[route]) return;
    
    const assets = this.routeAssets[route];
    let cachedAssets = 0;
    
    for (const asset of assets) {
      if (this.assetCache.has(asset)) {
        cachedAssets++;
      }
    }
    
    // Calcular porcentagem
    const cachePercentage = assets.length > 0 
      ? Math.round((cachedAssets / assets.length) * 100)
      : 0;
    
    // Atualizar status do cache
    this.cacheStatus[route] = {
      lastUpdated: Date.now(),
      cachePercentage,
      cachedAssets,
      totalAssets: assets.length
    };
  }
  
  /**
   * Pré-carrega todos os assets de uma rota específica
   * @param {string} route - Rota para pré-carregar
   * @returns {Promise<Object>} - Status do pré-carregamento
   */
  async preloadRouteAssets(route) {
    // Verificar se a rota existe em nossa configuração
    if (!this.routeAssets[route]) {
      console.warn(`Rota ${route} não encontrada na configuração de assets`);
      return { 
        success: false, 
        cachePercentage: 0,
        totalAssets: 0,
        cachedAssets: 0
      };
    }
    
    // Obter assets da rota
    const assets = this.routeAssets[route];
    
    // Separar por prioridade
    const highPriorityAssets = assets.filter(asset => this.getAssetPriority(asset) === 'high');
    const mediumPriorityAssets = assets.filter(asset => this.getAssetPriority(asset) === 'medium');
    const lowPriorityAssets = assets.filter(asset => this.getAssetPriority(asset) === 'low');
    
    // Determinar nível de concorrência ideal
    const concurrency = this.getOptimalConcurrency();
    
    try {
      // Carregar primeiro os assets de alta prioridade
      await this.loadAssetsWithConcurrency(highPriorityAssets, concurrency);
      
      // Depois os de média prioridade
      await this.loadAssetsWithConcurrency(mediumPriorityAssets, concurrency);
      
      // Por último os de baixa prioridade
      await this.loadAssetsWithConcurrency(lowPriorityAssets, concurrency);
      
      // Atualizar e retornar status de cache
      this.updateCacheStatus(route);
      
      return {
        success: true,
        ...this.cacheStatus[route]
      };
    } catch (error) {
      console.error(`Erro ao pré-carregar assets da rota ${route}:`, error);
      
      // Atualizar status mesmo em caso de erro
      this.updateCacheStatus(route);
      
      return {
        success: false,
        error: error.message,
        ...this.cacheStatus[route]
      };
    }
  }
  
  /**
   * Obtém o status do cache para uma rota específica
   * @param {string} route - Rota para verificar
   * @returns {Object} - Status do cache
   */
  getRouteAssetsStatus(route) {
    // Se a rota não existe em nossa configuração, retornar status vazio
    if (!this.routeAssets[route]) {
      return {
        cachePercentage: 0,
        cachedAssets: 0,
        totalAssets: 0,
        lastUpdated: null
      };
    }
    
    // Se já temos status em cache, retornar
    if (this.cacheStatus[route]) {
      return this.cacheStatus[route];
    }
    
    // Calcular status atual
    this.updateCacheStatus(route);
    
    return this.cacheStatus[route];
  }
  
  /**
   * Libera memória removendo assets do cache que não são mais necessários
   * @param {Array<string>} keepRoutes - Rotas para manter em cache
   * @returns {number} - Número de assets liberados
   */
  clearUnusedAssets(keepRoutes = []) {
    // Adicionar rotas essenciais para sempre manter
    const essentialRoutes = ['/', '/tela1', '/tela1-1'];
    const allKeepRoutes = [...new Set([...essentialRoutes, ...keepRoutes])];
    
    const allKeepAssets = allKeepRoutes.reduce((assets, route) => {
      if (this.routeAssets[route]) {
        return [...assets, ...this.routeAssets[route]];
      }
      return assets;
    }, []);
    
    let freedCount = 0;
    
    // Remover assets que não estão nas rotas para manter
    for (const [asset] of this.assetCache) {
      if (!allKeepAssets.includes(asset)) {
        this.assetCache.delete(asset);
        freedCount++;
      }
    }
    
    return freedCount;
  }
}

// Exportar instância única
const assetPreloader = new AssetPreloader();
export default assetPreloader; 